import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetMaterialsQuery, useDeleteMaterialMutation } from '../../redux/api/materialsApi';
import { getImageUrl } from '../../utils/imageUtils';
import { 
  Pagination, 
  Table, 
  Button, 
  Tag, 
  Typography, 
  Alert, 
  Space, 
  Modal,
  Image
} from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

const { Title, Text } = Typography;

const MaterialsPage = () => {
    const navigate = useNavigate();
    const { data: materials = [], isLoading, error } = useGetMaterialsQuery();
    const [deleteMaterial] = useDeleteMaterialMutation();
    
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [materialToDelete, setMaterialToDelete] = useState(null);

    const handleDelete = (material) => {
        setMaterialToDelete(material);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (materialToDelete) {
            try {
                await deleteMaterial(materialToDelete.id).unwrap();
                toast.success('Material deleted successfully!');
                setShowDeleteModal(false);
                setMaterialToDelete(null);
            } catch (err) {
                toast.error(err?.data?.message || err?.error || 'Failed to delete material');
            }
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setMaterialToDelete(null);
    };

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginatedMaterials = materials.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(materials.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Table columns configuration
    const columns = [
        {
            title: 'Image',
            key: 'image',
            width: '10%',
            render: (_, record) => (
                record.image_path ? (
                    <Image
                        src={getImageUrl(record.image_path)}
                        alt={record.name}
                        style={{ height: '70px', objectFit: 'cover', borderRadius: '4px' }}
                        preview={false}
                    />
                ) : (
                    <Text type="secondary">No image</Text>
                )
            ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: '12%',
            render: (code) => code || <Text type="secondary">-</Text>,
        },
        {
            title: 'Color',
            dataIndex: 'color',
            key: 'color',
            width: '12%',
            render: (color) => color || <Text type="secondary">-</Text>,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: '12%',
            render: (type) => type || <Text type="secondary">-</Text>,
        },
        {
            title: 'Style',
            dataIndex: 'style',
            key: 'style',
            width: '12%',
            render: (style) => style || <Text type="secondary">-</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            width: '10%',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '12%',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/materials/${record.id}`)}
                        title="View"
                    >
                        View
                    </Button>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/materials/${record.id}/edit`)}
                        title="Edit"
                    >
                        Edit
                    </Button>
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                        title="Delete"
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Alert title="Loading materials..." type="info" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="Error loading materials"
                description={error?.data?.message || error?.error || 'Failed to load materials'}
                type="error"
                showIcon
            />
        );
    }

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={1} style={{ margin: 0 }}>Materials</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate("/materials/add")}
                >
                    Add Material
                </Button>
            </div>

            {materials.length === 0 ? (
                <Alert
                    message="No materials found"
                    description="No materials available."
                    type="info"
                    action={
                        <Button size="small" onClick={() => navigate("/materials/add")}>
                            Add your first material
                        </Button>
                    }
                    showIcon
                />
            ) : (
                <>
                    <Table
                        columns={columns}
                        dataSource={paginatedMaterials}
                        rowKey="id"
                        pagination={false}
                        size="middle"
                        rowClassName={(record) => (!record.is_active ? 'ant-table-row-disabled' : '')}
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={{ marginTop: 16 }}>
                            <Pagination
                                current={currentPage}
                                total={materials.length}
                                pageSize={itemsPerPage}
                                onChange={handlePageChange}
                                hideOnSinglePage={true}
                                showSizeChanger={false}
                            />
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    <Modal
                        title="Confirm Delete"
                        open={showDeleteModal}
                        onOk={handleConfirmDelete}
                        onCancel={handleCancelDelete}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                    >
                        <div style={{ textAlign: 'center' }}>
                            <p>Are you sure you want to delete the material:</p>
                            <Title level={4} style={{ marginBottom: 16 }}>{materialToDelete?.name}</Title>
                            {materialToDelete?.image_path && (
                                <Image
                                    src={getImageUrl(materialToDelete.image_path)}
                                    alt={materialToDelete.name}
                                    style={{ height: "100px", objectFit: "cover", borderRadius: "4px" }}
                                    preview={false}
                                />
                            )}
                        </div>
                    </Modal>
                </>
            )}
        </div>
    );
};

export default MaterialsPage;
